/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import { useToasts } from "react-toast-notifications";
import { LayoutContext } from "../../components/layout";
import fetchHelper from "../../services/fetchHelper";
import "./PatientEngagementModal.scss";
import cn from "classnames";

const PatientEngagementModal = ({ task, onClose, refresh }) => {
  const { addToast } = useToasts();
  const { user } = useContext(LayoutContext);
  const [currentTask, setCurrentTask] = useState(task);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [disableMarkAsDone, setDisableMarkAsDone] = useState(true);
  const [isContacted, setIsContacted] = useState("");
  const [note, setNote] = useState("");
  const [error, setErrors] = useState({
    isContacted: false,
    note: false,
  });

  const close = (shouldRefresh = false) => {
    onClose(shouldRefresh);
  };

  function displayError(error) {
    if (Array.isArray(error.errors)) {
      error.errors.forEach((err) => {
        addToast(err.msg, {
          appearance: "error",
          autoDismiss: false,
        });
      });
    }
  }

  useEffect(() => {
    setDisableMarkAsDone(shouldDisableMarkAsDone());
  }, [currentTask, isLoading]);

  const markAsDone = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/engagement/task/${currentTask.taskId}/close`, {
        wasPatientContacted: isContacted === "yes",
        finalNote: note,
      });
      close(true);
    } catch (error) {
      displayError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const unassign = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/engagement/task/${currentTask.taskId}/unassign`);
      refresh();
      setCurrentTask({ ...currentTask, taskAssigned: false, taskAssignUserId: null });
    } catch (error) {
      displayError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const assign = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      await fetchHelper.put(
        `/patientmanagement/rest/engagement/task/${currentTask.taskId}/assignTo/${user.id}`
      );
      refresh();
      setCurrentTask({
        ...currentTask,
        taskAssigned: true,
        taskAssignUserId: user.id,
        taskAssignUserFirstName: user.name,
        taskAssignUserLastName: user.family,
      });
    } catch (error) {
      displayError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!isSubmitted) {
      setIsSubmitted(true);
    }

    setErrors({ ...error, isContacted: !!isContacted });
    setErrors({ ...error, note: !!note });

    if (isContacted && note) {
      markAsDone();
    }
  };

  const shouldDisableMarkAsDone = () => {
    const isAssignedToSameUser = !currentTask.taskAssigned || currentTask.taskAssignUserId !== user.id;
    return isLoading || isAssignedToSameUser;
  };

  const getPhoneLabel = () => {
    let label = "";

    if (task.homeNumber || task.phoneNumber) {
      label += task.homeNumber ? "Home" : "Mobile";
    }

    return (label += " Phone");
  };

  return (
    <div className="modal override-row cpopup show" id="patient-engagement-modal">
      <div className="modalContent">
        <div className="modalTop">
          <h2>{`${task.patientLastName}, ${task.patientFirstName}`}</h2>
          <button
            id="unassign-btn"
            className="btn btn-yellow"
            disabled={isLoading}
            onClick={currentTask.taskAssigned ? unassign : assign}
          >
            {currentTask.taskAssigned ? "Unreserve" : "Reserve"}
          </button>
        </div>
        <div className="modalBody">
          {task.homeNumber && task.phoneNumber ? (
            <>
              <div className="row no-divider-line">
                <div className="col">
                  <p id="pe-home-phone-number">
                    <span>Home Phone</span> {task.homeNumber}
                  </p>
                </div>
                <div className="col">
                  <p id="pe-mobile-phone-number">
                    <span>Mobile Phone</span> {task.phoneNumber}
                  </p>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <p id="pe-device-mfr">
                    <span>Device MFR</span> {task.deviceManufacturerName}
                  </p>
                </div>
                <div className="col">
                  <p id="pe-device-type">
                    <span>Device Type</span> {task.deviceType}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="row">
              <div className="col">
                <p id="pe-phone-number">
                  <span>{getPhoneLabel()}</span>
                  {task.homeNumber || task.phoneNumber || "-"}
                </p>
              </div>
              <div className="col">
                <p id="pe-device-mfr">
                  <span>Device MFR</span> {task.deviceManufacturerName}
                </p>
              </div>
              <div className="col">
                <p id="pe-device-type">
                  <span>Device Type</span> {task.deviceType}
                </p>
              </div>
            </div>
          )}
          <div className="row">
            <div className="col">
              <p>
                <span>Reason</span>
              </p>
              <p id="pe-reason">{task.taskReason}</p>
              <br />
              <p>
                <span>Initial Notes</span>
              </p>
              <p id="pe-initial-notes" className="allow-line-breaks">
                {task.taskInitialNote}
              </p>
            </div>
          </div>
          {currentTask.taskAssigned && (
            <>
              <p>
                Was the patient contacted? <span className="required-field">*</span>
              </p>
              <div className="is-contacted-wrapper">
                <label
                  className={cn({ "unassigned-task-modal": disableMarkAsDone }, "flex0 checkbox-container")}
                >
                  <input
                    id="pe-contacted-patient-yes"
                    type="radio"
                    name="contacted"
                    className="chk"
                    value="yes"
                    checked={isContacted === "yes"}
                    disabled={disableMarkAsDone}
                    onChange={(event) => setIsContacted(event.target.value)}
                  />
                  <span className="chkmrk radio" />
                  <span className="chk-label">Yes</span>
                </label>
                <label
                  className={cn({ "unassigned-task-modal": disableMarkAsDone }, "flex0 checkbox-container")}
                >
                  <input
                    id="pe-contacted-patient-no"
                    type="radio"
                    name="contacted"
                    className="chk"
                    value="no"
                    checked={isContacted === "no"}
                    disabled={disableMarkAsDone}
                    onChange={(event) => setIsContacted(event.target.value)}
                  />
                  <span className="chkmrk radio" />
                  <span className="chk-label">No</span>
                </label>
              </div>
              <p>
                Closing notes <span className="required-field">*</span>
              </p>
              <textarea
                id="pe-closing-notes"
                maxLength="1000"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                style={{ width: "100%", marginBottom: 0 }}
                disabled={disableMarkAsDone}
                className={cn({ "unassigned-task-modal": disableMarkAsDone }, "task-modal-notes")}
                placeholder="Notes about how the task was closed."
              />
              <div className="logo-notes engagement">
                <span>Max length: 1000 characters.</span>
              </div>
              <h5 className="reserved-user">{`Reserved by: ${currentTask.taskAssignUserLastName}, ${currentTask.taskAssignUserFirstName}`}</h5>
            </>
          )}
          <h5 className="reserved-user">{`Submitted by: ${currentTask.creatorUserLastName}, ${currentTask.creatorUserFirstName}`}</h5>
          {(!isContacted || !note) && isSubmitted && (
            <span style={{ display: "block", marginBottom: "25px" }} className="error">
              Required fields missing data. Please review and re-submit.
            </span>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
          <button
            className="btn"
            style={{ marginRight: "10px" }}
            disabled={isLoading}
            onClick={() => close(false)}
            id="close-btn"
          >
            Back
          </button>
          <button
            className="btn btnTeal"
            onClick={handleSubmit}
            disabled={disableMarkAsDone}
            id="mark-as-done-btn"
          >
            Mark as Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientEngagementModal;
